"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, Send, Phone, CheckCheck, ShieldCheck, MessageCircle } from "lucide-react";
import { WHATSAPP_TEMPLATES, fillTemplate } from "@/lib/demos/stockBrokingData";

export interface ChatContact {
  name: string;
  phone: string;
  client_code: string;
  seedInbound?: string; // an incoming message to pre-load
}

interface Message {
  id: string;
  from: "client" | "rm";
  body: string;
  time: string;
  delivered: boolean;
}

interface StockBrokingWhatsAppDrawerProps {
  contact: ChatContact | null;
  isOpen: boolean;
  onClose: () => void;
}

function nowTime() {
  return new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

export function StockBrokingWhatsAppDrawer({ contact, isOpen, onClose }: StockBrokingWhatsAppDrawerProps) {
  const reduceMotion = useReducedMotion();
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Seed the conversation whenever a new contact is opened.
  useEffect(() => {
    if (!contact) return;
    const base: Message[] = [
      {
        id: "seed-1",
        from: "rm",
        body: `Hi ${contact.name.split(" ")[0]}, this is your Relationship Manager at Johnson Securities (NSE·BSE·MCX·NSDL member). How can I help with your demat account today?`,
        time: "09:02",
        delivered: true,
      },
    ];
    if (contact.seedInbound) {
      base.push({ id: "seed-2", from: "client", body: contact.seedInbound, time: nowTime(), delivered: true });
    }
    setMessages(base);
    setDraft("");
  }, [contact]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: reduceMotion ? "auto" : "smooth" });
  }, [messages, reduceMotion]);

  const templates = useMemo(() => WHATSAPP_TEMPLATES, []);

  function send(body: string) {
    const trimmed = body.trim();
    if (!trimmed || !contact) return;
    setMessages((prev) => [
      ...prev,
      { id: `m-${Date.now()}`, from: "rm", body: trimmed, time: nowTime(), delivered: false },
    ]);
    setDraft("");
    // Simulate delivery ticks + a canned client acknowledgement.
    setTimeout(() => {
      setMessages((prev) => prev.map((m) => (m.delivered ? m : { ...m, delivered: true })));
    }, 700);
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: `c-${Date.now()}`,
          from: "client",
          body: "Thank you! 🙏",
          time: nowTime(),
          delivered: true,
        },
      ]);
    }, 1800);
  }

  return (
    <AnimatePresence>
      {isOpen && contact && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-slate-950/70 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.aside
            initial={reduceMotion ? { opacity: 0 } : { x: "100%" }}
            animate={reduceMotion ? { opacity: 1 } : { x: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="fixed top-0 right-0 z-[71] h-full w-full sm:max-w-md bg-white border-l border-slate-200 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="shrink-0 bg-gradient-to-r from-emerald-600 to-teal-700 px-4 py-3 flex items-center gap-3">
              <button onClick={onClose} className="text-white/80 hover:text-white p-1 rounded-lg transition-colors" aria-label="Close chat">
                <X className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-white truncate">{contact.name}</div>
                <div className="text-[11px] text-emerald-100/90 truncate">{contact.phone} · {contact.client_code}</div>
              </div>
              <a
                href={`tel:${contact.phone.replace(/\s/g, "")}`}
                className="p-2 rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors"
                title="Click to call"
              >
                <Phone className="w-4 h-4" />
              </a>
            </div>

            {/* Encryption notice */}
            <div className="shrink-0 bg-amber-500/10 text-amber-700/90 text-[10px] px-4 py-1.5 flex items-center justify-center gap-1.5 border-b border-slate-200">
              <ShieldCheck className="w-3 h-3" />
              End-to-end encrypted · WhatsApp Business API (demo)
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-3 py-4 space-y-2.5"
              style={{ backgroundColor: "#efeae2" }}
            >
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.from === "rm" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs leading-relaxed shadow-sm ${
                      m.from === "rm"
                        ? "bg-[#d9fdd3] text-slate-800 rounded-br-sm"
                        : "bg-white text-slate-800 rounded-bl-sm"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{m.body}</p>
                    <div className={`flex items-center gap-1 justify-end mt-1 text-[9px] ${m.from === "rm" ? "text-emerald-800/60" : "text-slate-400"}`}>
                      <span>{m.time}</span>
                      {m.from === "rm" && (
                        <CheckCheck className={`w-3 h-3 ${m.delivered ? "text-sky-600" : "text-slate-400"}`} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick templates */}
            <div className="shrink-0 border-t border-slate-200 bg-white px-3 py-2">
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setDraft(fillTemplate(t.body, contact))}
                    className="shrink-0 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-emerald-600/20 hover:text-emerald-700 text-slate-600 text-[10px] font-semibold whitespace-nowrap transition-colors border border-slate-300"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Composer */}
            <div className="shrink-0 border-t border-slate-200 bg-white p-3 flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(draft);
                  }
                }}
                rows={1}
                placeholder="Type a message…"
                className="flex-1 resize-none max-h-28 px-3 py-2 rounded-xl bg-slate-100 border border-slate-300 text-slate-900 text-xs placeholder:text-slate-400 focus:outline-none focus:border-emerald-500"
              />
              <button
                type="button"
                onClick={() => send(draft)}
                disabled={!draft.trim()}
                className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white flex items-center justify-center shrink-0 disabled:opacity-40 transition-all"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
